import { VideoChat } from '@/components/VideoChat';
import { MediaDevicesProvider } from '@/contexts/MediaDevicesContext';
import { SignalingProvider } from '@/contexts/SignalingContext';
import config from '@/config';
import { WebRTCProvider } from '@/contexts/WebRTCContext';
import React, { useEffect } from 'react';
import { Spinner } from '@/components/ui/spinner.tsx';
import { useBrowserSupport } from '@/hooks/useBrowserSupport.tsx';
import { BrowserNotSupportedOverlay } from '@/components/overlays/BrowserNotSupportedOverlay.tsx';
import { BanGuard } from '@/components/BanGuard.tsx';
import { GenderOnboarding } from '@/components/GenderOnboarding';
import { AgeGroupOnboarding } from '@/components/AgeGroupOnboarding';
import { AgeVerificationWarning } from '@/components/AgeVerificationWarning';
import { useAuth } from '@/contexts/AuthContext';
import { LandingPage } from '@/components/landing/LandingPage';

export default function Home() {
  const { isLoading: browserSupportLoading, error: browserSupportError, browserInfo } = useBrowserSupport();
  const { user, isLoading: authLoading, isAuthenticated, error: authError, login } = useAuth();

  // Construct WebSocket URL from baseUrl
  const wsUrl = React.useMemo(() => {
    const protocol = config.baseUrl.startsWith('https') ? 'wss' : 'ws';
    const baseUrlWithoutProtocol = config.baseUrl.replace(/^https?:\/\//, '');
    return `${protocol}://${baseUrlWithoutProtocol}/${config.endpoints.ws}`;
  }, []);

  // Handle OAuth callback - check for code in URL (for unauthenticated users)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const errorParam = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (errorParam) {
      console.error('Google OAuth error:', errorParam, errorDescription);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (code && !isAuthenticated && !authLoading) {
      console.log('Got authorization code from Google, exchanging for token');
      window.history.replaceState({}, document.title, window.location.pathname);
      login({
        code,
        redirectUri: config.google.oauth.redirectUrl,
        browserLocale: navigator.language
      });
    }
  }, [login, isAuthenticated, authLoading]);

  const handleGoogleLogin = () => {
    const { clientId, oauth } = config.google;
    const googleAuthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');

    googleAuthUrl.searchParams.set('client_id', clientId);
    googleAuthUrl.searchParams.set('redirect_uri', oauth.redirectUrl);
    googleAuthUrl.searchParams.set('scope', oauth.scope);
    googleAuthUrl.searchParams.set('response_type', 'code');
    googleAuthUrl.searchParams.set('state', crypto.randomUUID());
    googleAuthUrl.searchParams.set('access_type', 'offline');
    googleAuthUrl.searchParams.set('prompt', 'consent');

    window.location.href = googleAuthUrl.toString();
  };

  // Show loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0E1525] flex items-center justify-center">
        <Spinner size="large" variant="primary" text="Loading..." />
      </div>
    );
  }

  // Show landing page for unauthenticated users
  if (!isAuthenticated) {
    return (
      <LandingPage
        onGoogleLogin={handleGoogleLogin}
        authError={authError}
      />
    );
  }

  // Authenticated user flow - check browser support
  if (browserSupportLoading) {
    return (
      <div className="min-h-screen bg-[#0E1525] flex items-center justify-center">
        <Spinner size="large" variant="primary" text="Checking browser support..." />
      </div>
    );
  }

  if (browserSupportError) {
    return (
      <div className="min-h-screen bg-[#0E1525] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-2">Something went wrong</h1>
          <p className="text-gray-400">Please try again later</p>
        </div>
      </div>
    );
  }

  // Check if user needs to complete onboarding
  const hasGender = user?.maybeGenderInfoFE?.maybeDeclared;
  const hasAgeGroup = user?.maybeAgeInfoFE?.maybeDeclared;
  const needsGenderOnboarding = !hasGender as boolean;
  const needsAgeGroupOnboarding = (hasGender && !hasAgeGroup) as boolean;

  return (
    <div className="min-h-screen w-full bg-background">
      <BanGuard>
        {(needsGenderOnboarding || needsAgeGroupOnboarding) ? (
          <>
            {/* Show a placeholder background while onboarding */}
            <div className="min-h-screen bg-[#0E1525] flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-3xl font-bold text-white mb-2">Welcome to Video Chat</h1>
                <p className="text-gray-400">Complete your profile to get started</p>
              </div>
            </div>
            <GenderOnboarding
              isOpen={needsGenderOnboarding}
              onComplete={() => {
                // This will be handled by page reload in the component
              }}
            />
            <AgeGroupOnboarding
              isOpen={needsAgeGroupOnboarding}
              onComplete={() => {
                // This will be handled by page reload in the component
              }}
            />
          </>
        ) : (
          <>
            <AgeVerificationWarning />
            <SignalingProvider wsUrl={wsUrl}>
              <MediaDevicesProvider>
                <WebRTCProvider>
                  <VideoChat/>
                </WebRTCProvider>
              </MediaDevicesProvider>
            </SignalingProvider>
          </>
        )}
      </BanGuard>
      {/* Browser Not Supported Overlay */}
      <BrowserNotSupportedOverlay
        browserName={browserInfo.name}
        browserVersion={browserInfo.version}
        isVisible={!browserInfo.isSupported}
      />
    </div>
  );
}
